import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, User, Calendar, CreditCard, Check, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../supabase';
import AddressAutocomplete from './AddressAutocomplete';
import {
  carMakes, carModels, years,
  getOneTimeDates, formatOnetimeDateId, isClosedBookingDate, getOnetimeSlotId, formatOnetimeSlotLabel, markBookedDays,
  ONE_TIME_WEEKDAY_SLOTS, ONE_TIME_SATURDAY_SLOTS,
  MAINTENANCE_VEHICLE_TYPES, RESET_ADD_ONS, detectVehicleType, getNthWeekdayOfMonth,
} from '../data/constants';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const TRAVEL_FEE = 35;

const sanitize = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/[<>]/g, '').replace(/javascript:/gi, '').trim();
};

const STEPS = [
  { id: 1, label: 'Your Details', icon: User },
  { id: 2, label: 'Add-Ons', icon: Check },
  { id: 3, label: 'Pick a Date', icon: Calendar },
  { id: 4, label: 'Agreement & Pay', icon: CreditCard },
];

const COUNTIES = [
  { label: "St. Mary's County", value: "St. Mary's County" },
  { label: 'Calvert County', value: 'Calvert County' },
  { label: 'Charles County', value: 'Charles County' },
  { label: 'Other — travel fee applies (+$35)', value: 'outside' },
];

const COLORS = [
  'White','Pearl White','Black','Silver','Gray','Charcoal','Red','Blue','Navy',
  'Green','Brown','Beige','Champagne','Gold','Orange','Yellow','Purple','Maroon','Burgundy',
];

const fieldCls = (hasError) =>
  `w-full border-2 rounded-xl px-4 py-3 text-sm text-[#111827] focus:outline-none focus:ring-2 bg-white transition-all ${
    hasError
      ? 'border-red-500 ring-2 ring-red-200 bg-red-50'
      : 'border-gray-200 focus:border-[#68caf1] focus:ring-[#68caf1]/20'
  }`;

const getNthWeekdayLabel = (date) => {
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const nth = Math.ceil(date.getDate() / 7);
  const sfx = { 1: 'st', 2: 'nd', 3: 'rd' };
  return { dayName: DAYS[date.getDay()], nth, nthStr: `${nth}${sfx[nth] || 'th'}` };
};

const getNextRecurring = (date, count = 3) => {
  const { dayName, nth } = getNthWeekdayLabel(date);
  const results = [];
  let m = date.getMonth() + 1;
  let y = date.getFullYear();
  while (results.length < count) {
    if (m > 11) { m = 0; y++; }
    const candidate = getNthWeekdayOfMonth(y, m, nth, dayName.toLowerCase());
    if (candidate) results.push(candidate);
    m++;
  }
  return results;
};

function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const done = step > s.id;
        const active = step === s.id;
        return (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              active ? 'bg-[#68caf1] text-black' :
              done ? 'bg-[#68caf1]/20 text-[#68caf1]' :
              'bg-gray-100 text-gray-400'
            }`}>
              {done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{s.id}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px flex-1 max-w-8 ${step > s.id ? 'bg-[#68caf1]' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ErrorBanner({ errors }) {
  if (!errors.length) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex gap-3">
      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
      <ul className="space-y-1">
        {errors.map((e, i) => <li key={i} className="text-sm text-red-700">{e}</li>)}
      </ul>
    </div>
  );
}

// ── Step 2: Add-Ons ───────────────────────────────────────────────────────
function StepAddOns({ vehicleType, selectedAddOns, setSelectedAddOns }) {
  const vtInfo = MAINTENANCE_VEHICLE_TYPES.find(v => v.id === vehicleType) || MAINTENANCE_VEHICLE_TYPES[0];
  const toggle = (id) => setSelectedAddOns(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const addOnTotal = RESET_ADD_ONS.filter(a => selectedAddOns.includes(a.id)).reduce((s, a) => s + a.price, 0);
  const total = vtInfo.resetPrice + addOnTotal;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1">Customize Your Reset</h2>
        <p className="text-sm text-[#6B7280]">The base Alchemist Reset includes a full deep clean, shampoo, windows, and UV protectant. Add extras below to go deeper.</p>
      </div>

      <div className="space-y-3">
        {RESET_ADD_ONS.map(addon => {
          const selected = selectedAddOns.includes(addon.id);
          return (
            <button
              key={addon.id}
              type="button"
              onClick={() => toggle(addon.id)}
              className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                selected ? 'border-[#68caf1] bg-[#68caf1]/5' : 'border-gray-200 bg-white hover:border-[#68caf1]/50'
              }`}
            >
              <span className="text-2xl flex-shrink-0 mt-0.5">{addon.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#111827]">{addon.label}</p>
                <p className="text-xs text-[#6B7280] leading-relaxed mt-0.5">{addon.desc}</p>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="font-bold text-sm text-[#111827]">+${addon.price}</p>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-1 ml-auto ${
                  selected ? 'bg-[#68caf1] border-[#68caf1]' : 'border-gray-300 bg-white'
                }`}>
                  {selected && <Check className="w-3 h-3 text-black" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex justify-between items-center">
        <div>
          <p className="text-xs text-[#6B7280]">Reset total ({vtInfo.label})</p>
          <p className="text-sm font-bold text-[#111827]">${vtInfo.resetPrice} base{addOnTotal > 0 ? ` + $${addOnTotal} add-ons` : ''}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[#6B7280]">Deposit today (50%)</p>
          <p className="text-2xl font-black text-[#68caf1]">${Math.round(total / 2)}</p>
        </div>
      </div>
    </div>
  );
}

// ── Step 1: Contact + Vehicle ──────────────────────────────────────────────
function StepDetails({ form, setForm, errors, showErrors }) {
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: sanitize(val) }));

  const modelOptions = form.make && carModels[form.make]
    ? [...(carModels[form.make] || []), 'Other']
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1">Your Details</h2>
        <p className="text-sm text-[#6B7280]">We'll use this to confirm your appointment and send reminders.</p>
      </div>

      {showErrors && <ErrorBanner errors={errors} />}

      {/* Contact */}
      <div className="space-y-4">
        <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest">Contact Info</p>
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-1">Full Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            placeholder="Jane Smith"
            className={fieldCls(showErrors && !form.name.trim())}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1">Phone *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="3015559999"
              className={fieldCls(showErrors && (!form.phone.trim() || form.phone.length !== 10))}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="jane@email.com"
              className={fieldCls(showErrors && (!form.email.trim() || !EMAIL_REGEX.test(form.email.trim())))}
            />
          </div>
        </div>
        <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-xl border-2 transition-colors ${showErrors && !form.smsConsent ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-[#C9A84C]/50'}`}>
          <input
            type="checkbox"
            checked={form.smsConsent}
            onChange={e => set('smsConsent', e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#C9A84C] shrink-0"
          />
          <span className="text-xs text-gray-600 leading-relaxed">
            I agree to receive booking confirmations, service updates, and invoices via SMS from Monarch Detailing. Msg & data rates may apply. Reply <strong>STOP</strong> to unsubscribe at any time. *
          </span>
        </label>
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-1">Service Address *</label>
          <div className={showErrors && !form.address.trim() ? 'rounded-xl ring-2 ring-red-500' : ''}>
            <AddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onCountyDetected={county => setForm(prev => ({ ...prev, county }))}
              placeholder="Street address, City, MD"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-1">County *</label>
          <select
            value={form.county}
            onChange={e => set('county', e.target.value)}
            className={fieldCls(showErrors && !form.county)}
          >
            <option value="">Select county</option>
            {COUNTIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {form.county === 'outside' && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-amber-700 font-semibold text-xs">Outside Primary Service Area</p>
              <p className="text-amber-600 text-xs mt-0.5">A $35 travel fee will be added to your total.</p>
            </div>
          )}
        </div>
      </div>

      {/* Vehicle Type */}
      <div className="space-y-3">
        <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest">Vehicle Type *</p>
        {showErrors && !form.vehicleType && (
          <p className="text-xs text-red-600 font-medium">Please select your vehicle type</p>
        )}
        <div className="grid grid-cols-3 gap-3">
          {MAINTENANCE_VEHICLE_TYPES.map(vt => (
            <button
              key={vt.id}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, vehicleType: vt.id }))}
              className={`flex flex-col items-center py-3 px-2 rounded-xl border-2 text-center transition-all ${
                form.vehicleType === vt.id
                  ? 'bg-[#68caf1] border-[#68caf1] text-black'
                  : showErrors && !form.vehicleType
                  ? 'bg-red-50 border-red-300 text-[#374151] hover:border-[#68caf1]'
                  : 'bg-white border-gray-200 text-[#374151] hover:border-[#68caf1]'
              }`}
            >
              <span className="text-2xl mb-1">{vt.icon}</span>
              <span className="text-xs font-bold leading-tight">{vt.label}</span>
              <span className="text-xs mt-1 font-bold">${vt.resetPrice} reset</span>
              <span className="text-[10px] opacity-70">${vt.monthlyPrice}/mo after</span>
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle Info */}
      <div className="space-y-4">
        <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest">Vehicle Info</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1">Year *</label>
            <select
              value={form.year}
              onChange={e => set('year', e.target.value)}
              className={fieldCls(showErrors && !form.year)}
            >
              <option value="">Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#374151] mb-1">Color *</label>
            <select
              value={form.colorOther === 'other' ? 'other' : form.color}
              onChange={e => {
                const val = e.target.value;
                setForm(prev => ({
                  ...prev,
                  color: val === 'other' ? '' : val,
                  colorOther: val === 'other' ? 'other' : '',
                }));
              }}
              className={fieldCls(showErrors && !form.color.trim())}
            >
              <option value="">Select color</option>
              {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="other">Other (type yours)</option>
            </select>
            {form.colorOther === 'other' && (
              <input
                type="text"
                value={form.color}
                onChange={e => setForm(prev => ({ ...prev, color: sanitize(e.target.value) }))}
                placeholder="e.g. Midnight Blue"
                className="mt-2 w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#111827] focus:outline-none focus:border-[#68caf1] focus:ring-2 focus:ring-[#68caf1]/20 bg-white"
                autoFocus
              />
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-1">Make *</label>
          <select
            value={form.make}
            onChange={e => {
              const val = e.target.value;
              setForm(prev => ({ ...prev, make: val, model: '', makeOther: '' }));
            }}
            className={fieldCls(showErrors && !form.make)}
          >
            <option value="">Select make</option>
            {carMakes.map(m => <option key={m} value={m}>{m}</option>)}
            <option value="other">Other</option>
          </select>
          {form.make === 'other' && (
            <input
              type="text"
              value={form.makeOther || ''}
              onChange={e => setForm(prev => ({ ...prev, makeOther: sanitize(e.target.value) }))}
              placeholder="Type your vehicle make"
              className={`mt-2 ${fieldCls(showErrors && form.make === 'other' && !form.makeOther?.trim())}`}
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-1">Model *</label>
          {modelOptions.length > 0 ? (
            <select
              value={form.model}
              onChange={e => {
                const val = e.target.value;
                const detected = detectVehicleType(form.make, val);
                setForm(prev => ({ ...prev, model: val, modelOther: '', ...(detected ? { vehicleType: detected } : {}) }));
              }}
              className={fieldCls(showErrors && !form.model)}
            >
              <option value="">Select model</option>
              {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={form.model}
              onChange={e => setForm(prev => ({ ...prev, model: sanitize(e.target.value) }))}
              placeholder="Enter vehicle model"
              className={fieldCls(showErrors && !form.model.trim())}
            />
          )}
          {form.model === 'Other' && (
            <input
              type="text"
              value={form.modelOther || ''}
              onChange={e => setForm(prev => ({ ...prev, modelOther: sanitize(e.target.value) }))}
              placeholder="Type your vehicle model"
              className={`mt-2 ${fieldCls(showErrors && form.model === 'Other' && !form.modelOther?.trim())}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Date Picker ────────────────────────────────────────────────────
function StepDate({ selectedSlot, setSelectedSlot, errors, showErrors }) {
  const [bookedSlots, setBookedSlots] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  const dates = getOneTimeDates(2, 60);

  useEffect(() => {
    const loadBooked = async () => {
      try {
        const [{ data: mainData }, { data: oneTimeData }] = await Promise.all([
          supabase.from('maintenance_reset_slots').select('id').eq('is_booked', true),
          supabase.from('one_time_slots').select('id').eq('is_booked', true),
        ]);
        // One car per day: any booking (either calendar) fills that whole day.
        setBookedSlots(new Set(Object.keys(markBookedDays([
          ...(mainData || []).map(r => r.id),
          ...(oneTimeData || []).map(r => r.id),
        ]))));
      } catch {}
      setLoading(false);
    };
    loadBooked();
  }, []);

  const timeSlots = selectedDate
    ? (selectedDate.getDay() === 6 ? ONE_TIME_SATURDAY_SLOTS : ONE_TIME_WEEKDAY_SLOTS)
    : [];

  const groupedByMonth = dates.reduce((acc, date) => {
    const key = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (!acc[key]) acc[key] = [];
    acc[key].push(date);
    return acc;
  }, {});

  let recurringPreview = null;
  if (selectedSlot) {
    const datePart = selectedSlot.split('_')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    const slotDate = new Date(y, m - 1, d);
    const { dayName, nthStr } = getNthWeekdayLabel(slotDate);
    recurringPreview = { dayName, nthStr, nextDates: getNextRecurring(slotDate, 3) };
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1">Pick a Date</h2>
        <p className="text-sm text-[#6B7280]">Choose a day for your Alchemy Interior Reset. Your monthly maintenance will repeat on the same day every month — 1st Monday stays 1st Monday, 3rd Friday stays 3rd Friday.</p>
      </div>
      {showErrors && <ErrorBanner errors={errors} />}

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading availability…
        </div>
      ) : (
        <>
          <div className="space-y-6 max-h-80 overflow-y-auto pr-1">
            {Object.entries(groupedByMonth).map(([month, monthDates]) => (
              <div key={month}>
                <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-3">{month}</p>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {monthDates.map(date => {
                    const slots = date.getDay() === 6 ? ONE_TIME_SATURDAY_SLOTS : ONE_TIME_WEEKDAY_SLOTS;
                    const closed = isClosedBookingDate(date);
                    const allBooked = closed || slots.every(s => bookedSlots.has(getOnetimeSlotId(date, s.id)));
                    const isSelected = selectedDate && formatOnetimeDateId(date) === formatOnetimeDateId(selectedDate);
                    return (
                      <button
                        key={date.toISOString()}
                        onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                        disabled={allBooked}
                        className={`flex flex-col items-center py-2.5 px-1 rounded-xl text-xs font-bold transition-all border-2 ${
                          allBooked
                            ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                            : isSelected
                            ? 'bg-[#68caf1] border-[#68caf1] text-black'
                            : 'bg-white border-gray-200 text-[#374151] hover:border-[#68caf1] hover:text-[#111827]'
                        }`}
                      >
                        <span className="text-[10px] font-medium opacity-70">
                          {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                        <span className="text-base leading-none">{date.getDate()}</span>
                        {closed && <span className="text-[9px] font-medium leading-none mt-0.5">Closed</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {selectedDate && (
            <div>
              <p className="text-xs font-bold text-[#374151] uppercase tracking-widest mb-3">
                Available Times — {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              <div className="flex gap-3 flex-wrap">
                {timeSlots.map(slot => {
                  const slotId = getOnetimeSlotId(selectedDate, slot.id);
                  const dateClosed = isClosedBookingDate(selectedDate);
                  const booked = bookedSlots.has(slotId) || dateClosed;
                  const selected = selectedSlot === slotId;
                  return (
                    <button
                      key={slot.id}
                      disabled={booked}
                      onClick={() => setSelectedSlot(slotId)}
                      className={`flex flex-col items-center px-6 py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                        booked
                          ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                          : selected
                          ? 'bg-[#68caf1] border-[#68caf1] text-black'
                          : 'bg-white border-gray-200 text-[#374151] hover:border-[#68caf1]'
                      }`}
                    >
                      <span>{slot.time}</span>
                      {booked && <span className="text-[10px] font-normal mt-0.5">{dateClosed ? 'Closed' : 'Booked'}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {recurringPreview && (
            <div className="bg-[#68caf1]/10 border border-[#68caf1]/30 rounded-xl p-4 space-y-2">
              <p className="text-sm font-bold text-[#111827]">
                ✓ {formatOnetimeSlotLabel(selectedSlot)}
              </p>
              <p className="text-xs font-semibold text-[#374151]">
                📅 You chose the {recurringPreview.nthStr} {recurringPreview.dayName} — this becomes your monthly maintenance day
              </p>
              <div className="space-y-1 pt-1 border-t border-[#68caf1]/20">
                <p className="text-[10px] text-[#6B7280] font-bold uppercase tracking-widest">Your next 3 visits</p>
                {recurringPreview.nextDates.map((d, i) => (
                  <p key={i} className="text-xs text-[#6B7280]">
                    → {d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Step 4: Agreement + Pay ────────────────────────────────────────────────
function StepPay({ form, selectedSlot, selectedAddOns, agreed, setAgreed, signature, setSignature, errors, showErrors, promoCode = '', promoDiscount = 0 }) {
  const vehicleStr = [form.year, form.make === 'other' ? form.makeOther : form.make,
    form.model === 'Other' ? form.modelOther : form.model].filter(Boolean).join(' ');
  const vtInfo = MAINTENANCE_VEHICLE_TYPES.find(v => v.id === form.vehicleType) || MAINTENANCE_VEHICLE_TYPES[0];
  const addOnTotal = RESET_ADD_ONS.filter(a => selectedAddOns.includes(a.id)).reduce((s, a) => s + a.price, 0);
  const travelFee = form.county === 'outside' ? TRAVEL_FEE : 0;
  const resetPrice = vtInfo.resetPrice;
  const totalPriceBeforePromo = resetPrice + addOnTotal + travelFee;
  const promoSavings = promoDiscount > 0 ? Math.round(totalPriceBeforePromo * promoDiscount) : 0;
  const totalPrice = totalPriceBeforePromo - promoSavings;
  const depositPrice = Math.round(totalPrice / 2);
  const balancePrice = totalPrice - depositPrice;
  const monthlyPrice = vtInfo.monthlyPrice;

  let recurringPreview = null;
  if (selectedSlot) {
    const datePart = selectedSlot.split('_')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    const slotDate = new Date(y, m - 1, d);
    const { dayName, nthStr } = getNthWeekdayLabel(slotDate);
    recurringPreview = { dayName, nthStr, nextDates: getNextRecurring(slotDate, 3) };
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-1">Review & Pay</h2>
        <p className="text-sm text-[#6B7280]">Confirm your details, accept the agreement, and pay your deposit to lock in your date.</p>
      </div>
      {showErrors && <ErrorBanner errors={errors} />}

      {/* Summary */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest">Booking Summary</p>
        </div>
        <div className="px-5 divide-y divide-gray-100">
          {[
            ['Service', 'Alchemy Interior Reset'],
            ...(selectedAddOns.length > 0 ? [['Add-Ons', RESET_ADD_ONS.filter(a => selectedAddOns.includes(a.id)).map(a => a.label).join(', ')]] : []),
            ['Date', selectedSlot ? formatOnetimeSlotLabel(selectedSlot) : '—'],
            ...(recurringPreview ? [['Monthly Day', `${recurringPreview.nthStr} ${recurringPreview.dayName} of each month`]] : []),
            ['Location', form.address || '—'],
            ['Vehicle', vehicleStr || '—'],
            ['Name', form.name],
            ['Email', form.email],
          ].map(([label, val]) => (
            <div key={label} className="py-3 flex justify-between gap-4">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-semibold text-[#111827] text-right">{val}</span>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm text-[#374151]">Alchemist Reset ({vtInfo.label})</span>
            <span className="text-sm font-bold text-[#111827]">${resetPrice}.00</span>
          </div>
          {addOnTotal > 0 && (
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-[#374151]">Add-Ons</span>
              <span className="text-sm font-bold text-[#111827]">+${addOnTotal}.00</span>
            </div>
          )}
          {travelFee > 0 && (
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-[#374151]">Travel Fee</span>
              <span className="text-sm font-bold text-[#111827]">+${travelFee}.00</span>
            </div>
          )}
          {promoSavings > 0 && (
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm font-semibold text-emerald-600">🎉 {promoCode} promo (15% off)</span>
              <span className="text-sm font-bold text-emerald-600">−${promoSavings}.00</span>
            </div>
          )}
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
            <span className="text-sm font-bold text-[#374151]">Total</span>
            <span className="text-lg font-black text-[#111827]">${totalPrice}.00</span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-sm text-[#6B7280]">Deposit Due Today (50%)</span>
            <span className="text-base font-black text-[#68caf1]">${depositPrice}.00</span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-sm text-[#6B7280]">Balance Due at Service</span>
            <span className="text-sm font-bold text-[#9CA3AF]">${balancePrice}.00</span>
          </div>
        </div>
      </div>

      {/* Recurring schedule */}
      {recurringPreview && (
        <div className="bg-[#111827] rounded-2xl p-5 text-white">
          <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-2">Your Monthly Schedule</p>
          <p className="text-sm text-gray-300 mb-3">
            You chose the <strong className="text-white">{recurringPreview.nthStr} {recurringPreview.dayName}</strong> — Akeem returns this same day every month.
          </p>
          <div className="space-y-1.5">
            {recurringPreview.nextDates.map((d, i) => (
              <p key={i} className="text-xs text-gray-400">
                → {d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* After your reset */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
        <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-2">After Your Reset</p>
        <p className="text-sm text-[#374151] leading-relaxed">
          Monthly maintenance is <strong>${monthlyPrice}/month</strong> for your {vtInfo.label.toLowerCase()} — same address, same recurring day, every month. No condition charges. No upsells. ${monthlyPrice} is always ${monthlyPrice}.
        </p>
      </div>

      {/* Agreement */}
      <div className="space-y-4">
        <p className="text-xs font-bold text-[#374151] uppercase tracking-widest">Service Agreement</p>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-48 overflow-y-auto text-xs text-[#6B7280] leading-relaxed space-y-3">
          <p><strong>1. Service Description.</strong> The Alchemist Reset is a one-time deep interior detail service that establishes a clean baseline for your vehicle. Base pricing is vehicle-based: Sedan/Coupe $200, SUV/Crossover $215, Truck/Full-Size/Van $225. Optional add-ons are priced separately. A 50% deposit is due at booking; the remaining balance is collected at service.</p>
          <p><strong>2. Interior Maintenance Program.</strong> Following the Reset, a recurring monthly maintenance service is established at your vehicle's flat rate (Sedan $75/mo, SUV $80/mo, Truck $85/mo), regardless of interior condition during the maintenance period. Monthly services are lighter in scope than the Reset — they maintain the clean baseline already established. Either party may cancel the monthly program with 30 days written notice after the first service.</p>
          <p><strong>3. Service Location & Vehicle Access.</strong> Service is performed at the address provided. You agree to have the vehicle accessible at the scheduled time. Remove all personal belongings, valuables, car seats, and documents prior to service. Monarch Detailing is not responsible for lost, damaged, or stolen personal items left in the vehicle.</p>
          <p><strong>4. Inspection-Only Charges.</strong> Certain conditions can only be identified upon physical inspection and may result in additional charges. These include biohazard cleaning, heavy stain extraction beyond scope, excessive pet hair, and severe soiling. All charges are quoted on-site before work begins — you must approve before any additional work starts.</p>
          <p><strong>5. Pre-Existing Damage.</strong> Monarch Detailing is not liable for pre-existing scratches, swirl marks, paint chips, rust, trim damage, or any condition present before service begins.</p>
          <p><strong>6. Limitation of Liability.</strong> Monarch Detailing's maximum liability shall not exceed the total amount paid for that specific service.</p>
          <p><strong>7. Right to Refuse Service.</strong> Monarch Detailing reserves the right to refuse or discontinue service if the vehicle condition presents a health or safety risk.</p>
          <p><strong>8. Rescheduling & Cancellations.</strong> At least 48 hours' notice required to reschedule without forfeiting your deposit. Cancellations with less than 48 hours' notice may forfeit the deposit.</p>
          <p><strong>9. Force Majeure / Weather.</strong> Services may be rescheduled due to severe weather at no penalty to either party.</p>
          <p><strong>10. Quality Guarantee.</strong> If unsatisfied with any aspect of service, notify us within 24 hours and we will make it right.</p>
          <p><strong>11. SMS & Text Message Consent.</strong> By providing your phone number, you consent to receive SMS text messages from Monarch Detailing at the number provided. Messages may include appointment confirmations, en-route notifications, invoice delivery, and service-related updates. Message and data rates may apply. Reply STOP to any message to unsubscribe at any time.</p>
          <p><strong>12. Vehicle Photography.</strong> Monarch Detailing may photograph your vehicle before, during, and after service for quality assurance, documentation, and portfolio purposes. Photos may be used on social media and marketing materials. Vehicle identifying information (license plates) will be removed or obscured before any public use.</p>
          <p><strong>13. Electronic Signature.</strong> By signing below you confirm you are the registered owner or authorized agent for the vehicle and agree to all terms.</p>
        </div>

        <label className={`flex items-start gap-3 cursor-pointer rounded-lg p-3 border-2 transition-all ${
          showErrors && !agreed ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : 'border-transparent'
        }`}>
          <div
            onClick={() => setAgreed(!agreed)}
            className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center cursor-pointer transition-all ${
              agreed ? 'bg-[#68caf1] border-[#68caf1]' : showErrors ? 'border-red-500 bg-white' : 'border-gray-300 bg-white'
            }`}
          >
            {agreed && <Check className="w-3 h-3 text-black" />}
          </div>
          <span className="text-sm text-[#374151]">
            I have read and agree to the Interior Maintenance Service Agreement above.
          </span>
        </label>

        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-1">Digital Signature *</label>
          <p className="text-xs text-[#9CA3AF] mb-2">Type your full name as your electronic signature</p>
          <input
            type="text"
            value={signature}
            onChange={e => setSignature(sanitize(e.target.value))}
            placeholder="Jane Smith"
            className={`w-full border-2 rounded-xl px-4 py-3 text-sm text-[#111827] focus:outline-none focus:ring-2 bg-white font-medium transition-all ${
              showErrors && !signature.trim()
                ? 'border-red-500 ring-2 ring-red-200 bg-red-50'
                : 'border-gray-200 focus:border-[#68caf1] focus:ring-[#68caf1]/20'
            }`}
          />
        </div>
      </div>
    </div>
  );
}

// ── Main Wizard ─────────────────────────────────────────────────────────────
export default function MaintenanceBookingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get('test') === '1';
  const promoCode = searchParams.get('promo') || '';
  const promoDiscount = promoCode === 'FB15' ? 0.15 : 0;
  const [step, setStep] = useState(1);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', county: '',
    year: '', make: '', makeOther: '', model: '', modelOther: '',
    color: '', colorOther: '', vehicleType: '', smsConsent: false,
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState('');

  const getErrors = useCallback(() => {
    const errors = [];
    if (step === 1) {
      if (!form.name.trim()) errors.push('Full name is required');
      if (!form.phone.trim()) errors.push('Phone number is required');
      else if (form.phone.length !== 10) errors.push('Phone must be 10 digits');
      if (!form.smsConsent) errors.push('Please agree to receive SMS notifications');
      if (!form.email.trim()) errors.push('Email is required');
      else if (!EMAIL_REGEX.test(form.email.trim())) errors.push('Please enter a valid email address');
      if (!form.address.trim()) errors.push('Service address is required');
      if (!form.county) errors.push('Please select your county');
      if (!form.vehicleType) errors.push('Please select your vehicle type');
      const make = form.make === 'other' ? form.makeOther : form.make;
      const model = form.model === 'Other' ? form.modelOther : form.model;
      if (!make.trim()) errors.push('Vehicle make is required');
      if (!model.trim()) errors.push('Vehicle model is required');
      if (!form.year) errors.push('Vehicle year is required');
      if (!form.color.trim()) errors.push('Vehicle color is required');
    }
    if (step === 3) {
      if (!selectedSlot) errors.push('Please select a date and time for your reset');
    }
    if (step === 4) {
      if (!agreed) errors.push('You must accept the service agreement');
      if (!signature.trim()) errors.push('Digital signature is required');
    }
    return errors;
  }, [step, form, selectedSlot, agreed, signature]);

  const handleNext = () => {
    const errs = getErrors();
    if (errs.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setStep(s => s + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setShowErrors(false);
    setStep(s => s - 1);
    window.scrollTo(0, 0);
  };

  const handlePay = async () => {
    const errs = getErrors();
    if (errs.length) { setShowErrors(true); return; }
    setSubmitting(true);
    setApiError('');
    try {
      const make = form.make === 'other' ? form.makeOther : form.make;
      const model = form.model === 'Other' ? form.modelOther : form.model;

      const res = await fetch('/api/create-maintenance-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          county: form.county,
          year: form.year,
          make,
          model,
          color: form.color,
          vehicleType: form.vehicleType,
          slotId: selectedSlot,
          signature,
          addOns: selectedAddOns,
          travelFee: form.county === 'outside',
          testMode,
          promoCode: promoCode === 'FB15' ? promoCode : undefined, // campaign codes are one-time packages only
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setApiError(data.error || 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      window.location.href = data.url;
    } catch {
      setApiError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  const errors = getErrors();
  const activeVt = MAINTENANCE_VEHICLE_TYPES.find(v => v.id === form.vehicleType) || MAINTENANCE_VEHICLE_TYPES[0];
  const activeAddOnTotal = RESET_ADD_ONS.filter(a => selectedAddOns.includes(a.id)).reduce((s, a) => s + a.price, 0);
  const travelFee = form.county === 'outside' ? TRAVEL_FEE : 0;
  const activeTotalBeforePromo = activeVt.resetPrice + activeAddOnTotal + travelFee;
  const activeTotal = promoDiscount > 0
    ? activeTotalBeforePromo - Math.round(activeTotalBeforePromo * promoDiscount)
    : activeTotalBeforePromo;
  const depositDisplay = Math.round(activeTotal / 2);

  return (
    <div className="min-h-screen bg-[#F5F5F0] pb-24">
      <div className="max-w-lg mx-auto px-4 pt-8">
        {testMode && (
          <div className="mb-4 px-4 py-3 rounded-xl text-sm font-semibold text-center" style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' }}>
            🧪 TEST MODE — $1.00 total · $0.50 deposit · No real charge
          </div>
        )}
        <div className="text-center mb-6">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-1">Interior Maintenance · Booking</p>
          <h1 className="text-2xl font-bold text-[#111827]">Alchemy Interior Reset</h1>
          <p className="text-sm text-[#6B7280] mt-1">{testMode ? '🧪 Test: $1.00 · $0.50 deposit' : 'Sedan $200 · SUV $215 · Truck $225 · 50% deposit today · Then $75–$85/month'}</p>
        </div>

        <StepIndicator step={step} />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {step === 1 && <StepDetails form={form} setForm={setForm} errors={errors} showErrors={showErrors} />}
          {step === 2 && <StepAddOns vehicleType={form.vehicleType} selectedAddOns={selectedAddOns} setSelectedAddOns={setSelectedAddOns} />}
          {step === 3 && <StepDate selectedSlot={selectedSlot} setSelectedSlot={setSelectedSlot} errors={errors} showErrors={showErrors} />}
          {step === 4 && (
            <StepPay
              form={form} selectedSlot={selectedSlot} selectedAddOns={selectedAddOns}
              agreed={agreed} setAgreed={setAgreed} signature={signature} setSignature={setSignature}
              errors={errors} showErrors={showErrors}
              promoCode={promoCode} promoDiscount={promoDiscount}
            />
          )}

          {apiError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            {step > 1 ? (
              <button onClick={handleBack} className="flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-[#374151] hover:bg-gray-50 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <button onClick={() => navigate('/maintenance')} className="flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-[#374151] hover:bg-gray-50 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Cancel
              </button>
            )}

            {step < 4 ? (
              <button onClick={handleNext} className="flex-1 flex items-center justify-center gap-2 bg-[#68caf1] text-black font-bold py-3 rounded-xl hover:bg-[#5bb8db] transition-all active:scale-95">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handlePay}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 bg-[#68caf1] text-black font-bold py-3 rounded-xl hover:bg-[#5bb8db] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Taking you to payment…</>
                ) : (
                  <><CreditCard className="w-4 h-4" /> Pay ${depositDisplay} Deposit & Start My Program</>
                )}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-[#9CA3AF] mt-4 px-4">
          Secured by Stripe. Your card is saved for your monthly maintenance visits. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
